<template>
  <div class="sales-register">
    <!-- Quick periods -->
    <ReportChoices :choices="quickRanges" :active="activeRange" @select="applyRange" />

    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 4">
        <div class="rp-filter">
          <label>From date</label>
          <input v-model="filters.date_from" type="date" class="form-control" :max="filters.date_to || today" />
        </div>
        <div class="rp-filter">
          <label>To date</label>
          <input v-model="filters.date_to" type="date" class="form-control" :min="filters.date_from" :max="today" />
        </div>
        <div class="rp-filter">
          <label>Customer</label>
          <FormDropdown v-model="filters.customer" :options="customerOptions" placeholder="All customers" clearable />
        </div>
        <div class="rp-filter">
          <label>Sold by</label>
          <FormDropdown v-model="filters.cashier" :options="cashierOptions" placeholder="Everyone" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Invoice</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input
              v-model="filters.search"
              type="text"
              class="form-control"
              placeholder="Search invoice no., customer or phone"
            />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="hasActiveFilters"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <div class="rp-card tone-orange">
        <span class="rp-card-icon"><i class="ti ti-receipt-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Total Sales</div>
          <div class="rp-card-value">{{ formatMoney(summary.total) }}</div>
          <div class="rp-card-sub">{{ plural(summary.invoice_count, 'invoice') }} · avg {{ formatMoney(summary.avg_invoice) }}</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-discount-2"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Discount · VAT</div>
          <div class="rp-card-value">{{ formatMoney(summary.discount) }}</div>
          <div class="rp-card-sub">{{ summary.discount_rate }}% of sub-total · VAT {{ formatMoney(summary.vat) }}</div>
        </div>
      </div>
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: dueSelected }"
              :title="dueSelected ? 'Show all invoices' : 'Show invoices with due'" @click="toggleDue">
        <span class="rp-card-icon"><i class="ti ti-cash-banknote"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Collected · Due</div>
          <div class="rp-card-value">{{ formatMoney(summary.paid) }}</div>
          <div class="rp-card-sub" :class="{ 'text-danger': Number(summary.due) }">
            {{ formatMoney(summary.due) }} due on {{ plural(summary.due_count, 'invoice') }}
          </div>
        </div>
      </button>
      <div class="rp-card tone-teal">
        <span class="rp-card-icon"><i class="ti ti-receipt-refund"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Returned · Net</div>
          <div class="rp-card-value">{{ formatMoney(summary.net_amount) }}</div>
          <div class="rp-card-sub">
            after {{ formatMoney(summary.returned_amount) }} returned on {{ plural(summary.returned_count, 'invoice') }}
          </div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights trend-first" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title d-flex justify-content-between align-items-center">
          <span>Sales {{ summary.trend_unit === 'month' ? 'by month' : 'by day' }}</span>
          <span class="rp-legend">
            <span><i class="rp-dot" :style="{ background: SALES_COLOR }"></i>Sales</span>
            <span><i class="rp-dot" :style="{ background: COUNT_COLOR }"></i>Invoices</span>
          </span>
        </div>
        <div v-show="summary.trend.length" class="rp-chart"><canvas ref="trendChart"></canvas></div>
        <p v-if="!summary.trend.length" class="text-muted small mb-0">No sales in this period.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Payment methods</div>
        <div v-if="paymentBars.length" class="rp-bars">
          <div v-for="method in paymentBars" :key="method.label">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">
                <i class="ti me-1" :class="PAYMENT_ICONS[method.kind] || 'ti-credit-card'"></i>{{ method.label }}
              </span>
              <span class="fw-semibold text-nowrap">
                {{ formatMoney(method.amount) }} <span class="text-muted fw-normal">({{ method.share }}%)</span>
              </span>
            </div>
            <div class="rp-bar-track">
              <div class="rp-bar-fill" :class="PAYMENT_TONES[method.kind]" :style="{ width: `${method.percent}%` }"></div>
            </div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No payments in this period.</p>
        <div class="rp-note mt-3">
          Margin <strong>{{ summary.margin }}%</strong> · gross profit {{ formatMoney(summary.profit) }} on
          cost {{ formatMoney(summary.cost) }}
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Top customers</div>
        <ul v-if="summary.top_customers.length" class="rp-ranked">
          <li v-for="(customer, index) in summary.top_customers" :key="customer.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="customer.name">{{ customer.name }}</div>
              <small class="text-muted">{{ plural(customer.count, 'invoice') }}</small>
            </div>
            <span class="fw-semibold text-nowrap">{{ formatMoney(customer.total) }}</span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">No sales in this period.</p>
      </div>
    </div>

    <!-- Invoices table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in statusPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: (filters.status || 'all') === pill.key }" @click="setStatus(pill.key)">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th class="rp-expand-col"></th>
              <th v-for="column in columns" :key="column.key" :class="column.class" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="row in pagedRows" :key="row.key">
              <tr :class="{ expanded: expanded[row.key] }">
                <td class="rp-expand-col">
                  <button
                    type="button"
                    class="rp-expand"
                    :title="expanded[row.key] ? 'Hide items' : 'Show items'"
                    @click="toggleRow(row.key)"
                  >
                    <i class="ti" :class="expanded[row.key] ? 'ti-chevron-down' : 'ti-chevron-right'"></i>
                  </button>
                </td>
                <td class="text-nowrap">
                  {{ formatDate(row.date) }}
                  <span v-if="row.time" class="rp-sub">{{ row.time }}</span>
                </td>
                <td class="fw-semibold text-nowrap">{{ row.invoice_no }}</td>
                <td>
                  <div class="fw-medium">{{ row.customer }}</div>
                  <span v-if="showPhone(row.customer_phone)" class="rp-sub">{{ row.customer_phone }}</span>
                </td>
                <td class="text-center">
                  <span class="rp-count" :title="`${formatQty(row.qty)} units`">{{ row.item_count }}</span>
                </td>
                <td class="text-end">{{ formatMoney(row.sub_total) }}</td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.discount) }">
                  {{ Number(row.discount) ? `−${formatMoney(row.discount)}` : '—' }}
                </td>
                <td class="text-end" :class="{ 'text-muted': !Number(row.vat) }">
                  {{ Number(row.vat) ? formatMoney(row.vat) : '—' }}
                </td>
                <td class="text-end fw-semibold">{{ formatMoney(row.total) }}</td>
                <td class="text-end" :class="Number(row.due) ? 'text-danger fw-semibold' : 'text-muted'">
                  {{ Number(row.due) ? formatMoney(row.due) : '—' }}
                </td>
                <td class="rp-narration" :title="row.payment_method">{{ row.payment_method }}</td>
                <td>
                  <span class="rp-badge" :class="STATUS_TONES[row.payment_status]">{{ row.payment_label }}</span>
                  <span v-if="row.return_status" class="rp-badge info d-table mt-1">
                    <i class="ti ti-arrow-back-up"></i>{{ row.return_label }}
                  </span>
                </td>
              </tr>

              <tr v-if="expanded[row.key]" class="rp-detail-row">
                <td></td>
                <td :colspan="columns.length">
                  <div class="rp-detail">
                    <div class="rp-detail-title d-flex flex-wrap justify-content-between gap-2">
                      <span><i class="ti ti-file-invoice me-1"></i>Invoice {{ row.invoice_no }}</span>
                      <span class="text-muted fw-normal">
                        Sold by {{ row.created_by || '—' }}
                        <template v-if="row.payment_reference"> · Ref: {{ row.payment_reference }}</template>
                      </span>
                    </div>
                    <div class="table-responsive">
                      <table class="table table-sm mb-0">
                        <thead>
                          <tr>
                            <th>Item Code</th>
                            <th>Item Name</th>
                            <th>Variant</th>
                            <th>Batch</th>
                            <th class="text-end">Qty</th>
                            <th class="text-end">Unit Price</th>
                            <th class="text-end">Gross</th>
                            <th class="text-end">Discount</th>
                            <th class="text-end">VAT</th>
                            <th class="text-end">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="line in row.lines" :key="line.key">
                            <td class="fw-semibold">{{ line.item_code }}</td>
                            <td>
                              {{ line.item_name }}
                              <small v-if="line.sku" class="d-block text-muted">SKU: {{ line.sku }}</small>
                            </td>
                            <td>
                              <template v-if="line.variant">
                                <span v-for="part in line.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                              </template>
                              <span v-else class="text-muted">—</span>
                            </td>
                            <td>{{ line.batch_no || '—' }}</td>
                            <td class="text-end fw-semibold">
                              {{ formatQty(line.qty) }}<small v-if="line.unit" class="text-muted ms-1">{{ line.unit }}</small>
                            </td>
                            <td class="text-end">{{ formatMoney(line.unit_price) }}</td>
                            <td class="text-end">{{ formatMoney(line.gross) }}</td>
                            <td class="text-end">{{ Number(line.discount) ? `−${formatMoney(line.discount)}` : '—' }}</td>
                            <td class="text-end">{{ Number(line.vat) ? formatMoney(line.vat) : '—' }}</td>
                            <td class="text-end fw-semibold">{{ formatMoney(line.net) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div class="sr-totals">
                      <div v-for="item in invoiceTotals(row)" :key="item.label" :class="item.class">
                        <span>{{ item.label }}</span><b>{{ item.value }}</b>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length + 1">
                <div class="rp-empty">
                  <i class="ti ti-file-invoice"></i>
                  <div class="fw-semibold">No invoices found</div>
                  <small class="text-muted">Try another period, customer or status.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="rows.length">
            <tr>
              <th></th>
              <th colspan="3" class="text-end">Total ({{ plural(rows.length, 'invoice') }})</th>
              <th class="text-center">{{ formatQty(summary.qty) }}</th>
              <th class="text-end">{{ formatMoney(summary.sub_total) }}</th>
              <th class="text-end">{{ Number(summary.discount) ? `−${formatMoney(summary.discount)}` : '—' }}</th>
              <th class="text-end">{{ formatMoney(summary.vat) }}</th>
              <th class="text-end">{{ formatMoney(summary.total) }}</th>
              <th class="text-end text-danger">{{ formatMoney(summary.due) }}</th>
              <th colspan="2"></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Total = sub-total − discount + VAT, rounded as invoiced. Due is what was left unpaid at the time of sale;
      later due collections are recorded per customer.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml } from '../inventory/inventoryReportMixin';
import periodReport, { defaultPeriod } from '../inventory/periodReportMixin';
import salesReport, { COUNT_COLOR, PAYMENT_ICONS, PAYMENT_TONES, SALES_COLOR } from './salesReportMixin';

const EMPTY_SUMMARY = {
  qty: 0, sub_total: 0, discount: 0, vat: 0, rounding: 0, total: 0, paid: 0, due: 0, returned_amount: 0,
  net_amount: 0, cost: 0, profit: 0, invoice_count: 0, customer_count: 0, avg_invoice: 0, discount_rate: 0,
  margin: 0, due_count: 0, returned_count: 0, status_counts: { all: 0, paid: 0, partial: 0, unpaid: 0, returned: 0 },
  by_payment: [], top_customers: [], trend_unit: 'day', trend: [],
};

const STATUS_TONES = { paid: 'success', partial: 'warning', unpaid: 'danger' };

export default {
  name: 'SalesRegisterReport',
  mixins: [inventoryReport, periodReport, salesReport],

  reportUrl: apiUrls.sales_register_report.get,
  fileName: 'sales_register',
  columns: [
    { key: 'date', label: 'Date' },
    { key: 'invoice_no', label: 'Invoice' },
    { key: 'customer', label: 'Customer' },
    { key: 'item_count', label: 'Items', class: 'text-center', numeric: true },
    { key: 'sub_total', label: 'Sub Total', class: 'text-end', numeric: true },
    { key: 'discount', label: 'Discount', class: 'text-end', numeric: true },
    { key: 'vat', label: 'VAT', class: 'text-end', numeric: true },
    { key: 'total', label: 'Total', class: 'text-end', numeric: true },
    { key: 'due', label: 'Due', class: 'text-end', numeric: true },
    { key: 'payment_method', label: 'Payment' },
    { key: 'payment_label', label: 'Status' },
  ],

  data() {
    return {
      filters: {
        ...defaultPeriod(), customer: null, cashier: null, status: null,
        warehouse: null, category: null, sub_category: null, search: '',
      },
      summary: { ...EMPTY_SUMMARY },
      sortKey: null,
      sortDir: 'desc',
      SALES_COLOR,
      COUNT_COLOR,
      STATUS_TONES,
      PAYMENT_ICONS,
      PAYMENT_TONES,
    };
  },

  computed: {
    hasActiveFilters() {
      const { customer, cashier, status, search } = this.filters;
      return this.periodChanged || !!(customer || cashier || status || search.trim());
    },

    statusPills() {
      const counts = this.summary.status_counts;
      return [
        { key: 'all', label: 'All invoices', count: counts.all },
        { key: 'paid', label: 'Paid', count: counts.paid },
        { key: 'partial', label: 'Partly paid', count: counts.partial },
        { key: 'unpaid', label: 'Unpaid', count: counts.unpaid },
        { key: 'returned', label: 'With returns', count: counts.returned },
      ];
    },

    dueSelected() {
      return this.filters.status === 'partial' || this.filters.status === 'unpaid';
    },

    paymentBars() {
      const total = this.summary.by_payment.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
      return this.withPercent(this.summary.by_payment, 'amount').map((m) => ({
        ...m, share: total ? Math.round(((Number(m.amount) || 0) / total) * 100) : 0,
      }));
    },

    visibleRows() {
      // Without a chosen column the server's newest-first order is kept.
      return this.sortKey ? this.sortRows(this.rows) : this.rows;
    },
  },

  watch: {
    'filters.cashier'() { this.onFilterChange(); },
    'filters.status'() { this.onFilterChange(); },
  },

  methods: {
    extraParams() {
      const { cashier, status } = this.filters;
      return {
        ...this.periodParams(),
        ...this.customerParams(),
        ...(cashier ? { cashier } : {}),
        ...(status ? { status } : {}),
      };
    },

    resetExtraFilters() {
      this.sortKey = null;
      return { ...defaultPeriod(this.today), customer: null, cashier: null, status: null };
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
      this.$nextTick(() => this.drawMoneyTrend(this.$refs.trendChart, [
        { label: 'Sales', field: 'total', color: SALES_COLOR },
        { label: 'Invoices', field: 'count', color: COUNT_COLOR, count: true },
      ]));
    },

    setStatus(key) {
      this.filters.status = key === 'all' ? null : key;
    },

    toggleDue() {
      this.filters.status = this.dueSelected ? null : 'partial';
    },

    showPhone(phone) {
      return phone && !/^0+$/.test(phone);
    },

    invoiceTotals(row) {
      const items = [
        { label: 'Sub-total', value: this.formatMoney(row.sub_total) },
        ...(Number(row.discount) ? [{ label: 'Discount', value: `−${this.formatMoney(row.discount)}` }] : []),
        ...(Number(row.vat) ? [{ label: 'VAT', value: this.formatMoney(row.vat) }] : []),
        ...(Number(row.rounding) ? [{ label: 'Rounding', value: this.signedMoney(row.rounding) }] : []),
        { label: 'Total', value: this.formatMoney(row.total), class: 'is-total' },
        ...row.payments.map((p) => ({
          label: p.kind === 'due' ? 'Due' : `Paid · ${p.label}`,
          value: this.formatMoney(p.amount),
          class: p.kind === 'due' ? 'text-danger' : '',
        })),
      ];
      if (Number(row.returned_amount)) {
        items.push({ label: `Returned (${this.formatQty(row.returned_qty)})`, value: `−${this.formatMoney(row.returned_amount)}`, class: 'text-info' });
        items.push({ label: 'Net after returns', value: this.formatMoney(row.net_amount), class: 'is-total' });
      }
      return items;
    },

    statusText(row) {
      return row.payment_label + (row.return_label ? ` · ${row.return_label}` : '');
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${this.formatDate(r.date)} ${escapeHtml(r.time)}</td>
          <td>${escapeHtml(r.invoice_no)}</td>
          <td>${escapeHtml(r.customer)}</td>
          <td class="num">${r.item_count}</td>
          <td class="num">${this.formatMoney(r.sub_total)}</td>
          <td class="num">${this.formatMoney(r.discount)}</td>
          <td class="num">${this.formatMoney(r.vat)}</td>
          <td class="num">${this.formatMoney(r.total)}</td>
          <td class="num">${this.formatMoney(r.due)}</td>
          <td>${escapeHtml(r.payment_method)}</td>
          <td>${escapeHtml(this.statusText(r))}</td>
        </tr>`).join('');
      const table = `<table><thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th class="num">Items</th>
          <th class="num">Sub Total</th><th class="num">Discount</th><th class="num">VAT</th><th class="num">Total</th>
          <th class="num">Due</th><th>Payment</th><th>Status</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="4" class="num">Total</td><td class="num">${this.formatMoney(s.sub_total)}</td>
          <td class="num">${this.formatMoney(s.discount)}</td><td class="num">${this.formatMoney(s.vat)}</td>
          <td class="num">${this.formatMoney(s.total)}</td><td class="num">${this.formatMoney(s.due)}</td>
          <td colspan="2"></td></tr></tfoot></table>`;
      const status = this.statusPills.find((p) => p.key === (this.filters.status || 'all'))?.label;
      const search = this.filters.search.trim();
      this.openPrint(
        'Sales Register',
        [
          [this.periodText(), `Status: ${status}`, this.customerText(),
            this.cashierText(), this.storeText(),
            ...(search ? [`Search: ${search}`] : [])].join(' · '),
          `Printed ${new Date().toLocaleString()}`,
        ],
        [
          ['Total Sales', `${this.formatMoney(s.total)} · ${this.plural(s.invoice_count, 'invoice')}`],
          ['Discount · VAT', `${this.formatMoney(s.discount)} · ${this.formatMoney(s.vat)}`],
          ['Collected · Due', `${this.formatMoney(s.paid)} · ${this.formatMoney(s.due)}`],
          ['Returned · Net', `${this.formatMoney(s.returned_amount)} · ${this.formatMoney(s.net_amount)}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="../inventory/inventoryReport.css"></style>

<style scoped>
.sr-totals {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem 1.5rem;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed #EEF0F3;
  font-size: 0.8rem;
}

.sr-totals > div {
  display: flex;
  gap: 0.5rem;
  color: #646B72;
}

.sr-totals b {
  color: #092C4C;
}

.sr-totals .is-total b {
  color: #D9771E;
}

.sr-totals .text-danger b,
.sr-totals .text-info b {
  color: inherit;
}
</style>
